import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { getPresignedLogoUploadUrl, uploadFileToS3 } from '../../utils/s3Api';
import LoadingSpinner from '../common/LoadingSpinner';

const UploadContainer = styled.div`
  border: 2px dashed ${props => props.isDragActive ? '#2196f3' : '#ccc'};
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  background: ${props => props.isDragActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    border-color: #2196f3;
    background: rgba(33, 150, 243, 0.05);
  }
`;

const UploadText = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  margin-top: 10px;
  border-radius: 4px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`;

const ErrorText = styled.p`
  color: #f44336;
  margin: 5px 0 0;
  font-size: 12px;
`;

const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp']
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function SponsorLogoUpload({ onUploadComplete }) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file
    if (!ACCEPTED_TYPES[file.type]) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 5MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Get presigned URL and upload to S3
      const { uploadUrl } = await getPresignedLogoUploadUrl(file.name);

      // Extract key from uploadUrl if not provided by API
      const key = uploadUrl.split('?')[0].split('/').slice(-2).join('/');

      await uploadFileToS3(uploadUrl, file);

      // Pass the extracted key
      onUploadComplete?.(key);
    } catch (err) {
      setError('Failed to upload logo. Please try again.');
      toast.error('Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  }, [onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,

    multiple: false
  });

  return (
    <UploadContainer {...getRootProps()} isDragActive={isDragActive}>
      <input {...getInputProps()} />
      {isUploading ? (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      ) : (
        <>
          <UploadText>
            {isDragActive
              ? 'Drop the logo here...'
              : 'Drag and drop a logo here, or click to select'}
          </UploadText>
          {preview && <PreviewImage src={preview} alt="Logo preview" />}
          {error && <ErrorText>{error}</ErrorText>}
        </>
      )}
    </UploadContainer>
  );
} 